import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  IconButton,
  Typography,
  Box,
} from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import InputText from "components/_refactor/text/input";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import { columnTextField } from "utils/tables-utils";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import themeColorsDesingSystem from "assets/theme/ds-colors";
import {
  listPlaylists,
  createPlaylist,
  editPlaylist,
  listContents,
  addPlaylistItem,
  removePlaylistItem,
} from "../services/franchise-marketing-api";

const STATUS_OPTIONS = [
  { value: true, label: "Ativo" },
  { value: false, label: "Inativo" },
];

const EMPTY_FORM = {
  nome: "",
  descricao: "",
  ativo: STATUS_OPTIONS[0],
  ordem: "",
};

export default function DialogPlaylists({ open, onClose }) {
  const snackbar = useSnackbar();
  const [playlists, setPlaylists] = useState([]);
  const [videoOptions, setVideoOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [videoToAdd, setVideoToAdd] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [playlistResult, contents] = await Promise.all([
        listPlaylists(false),
        listContents({ tipo: "Video" }),
      ]);
      setPlaylists(playlistResult);
      setVideoOptions(
        contents.map((c) => ({
          value: c.IdFranquiaMarketingConteudo,
          label: c.Titulo,
        })),
      );
    } catch (error) {
      console.error(error);
      snackbar.showMessage(error?.message || "Falha ao carregar playlists.", {
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadData();
    } else {
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      setSelectedPlaylist(null);
    }
  }, [open]);

  const handleSave = async () => {
    if (!form.nome?.trim()) {
      snackbar.showMessage("Nome da playlist é obrigatório.", {
        severity: "error",
      });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nome: form.nome.trim(),
        descricao: form.descricao,
        ordem: Number(form.ordem) || 0,
        ativo: form.ativo?.value ?? true,
      };

      if (editingId) {
        await editPlaylist({
          ...payload,
          idFranquiaMarketingPlaylist: editingId,
        });
        snackbar.showMessage("Playlist atualizada.", { severity: "success" });
      } else {
        await createPlaylist(payload);
        snackbar.showMessage("Playlist criada.", { severity: "success" });
      }

      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      await loadData();
    } catch (error) {
      snackbar.showMessage(error?.message || "Falha ao salvar playlist.", {
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddVideo = async () => {
    if (!selectedPlaylist || !videoToAdd?.value) return;

    try {
      await addPlaylistItem(
        selectedPlaylist.IdFranquiaMarketingPlaylist,
        videoToAdd.value,
      );
      snackbar.showMessage("Vídeo adicionado à playlist.", {
        severity: "success",
      });
      setVideoToAdd(null);
      await loadData();
      const updated = (await listPlaylists(false)).find(
        (p) =>
          p.IdFranquiaMarketingPlaylist ===
          selectedPlaylist.IdFranquiaMarketingPlaylist,
      );
      setSelectedPlaylist(updated || null);
    } catch (error) {
      snackbar.showMessage(error?.message || "Falha ao adicionar vídeo.", {
        severity: "error",
      });
    }
  };

  const handleRemoveItem = async (item) => {
    try {
      await removePlaylistItem(item.IdFranquiaMarketingPlaylistItem);
      snackbar.showMessage("Vídeo removido da playlist.", {
        severity: "success",
      });
      await loadData();
      const updated = (await listPlaylists(false)).find(
        (p) =>
          p.IdFranquiaMarketingPlaylist ===
          selectedPlaylist?.IdFranquiaMarketingPlaylist,
      );
      setSelectedPlaylist(updated || null);
    } catch (error) {
      snackbar.showMessage(error?.message || "Falha ao remover vídeo.", {
        severity: "error",
      });
    }
  };

  const playlistColumns = [
    columnTextField({ dataField: "Nome", text: "Playlist", align: "left" }),
    columnTextField({
      dataField: "Descricao",
      text: "Descrição",
      align: "left",
    }),
    columnTextField({
      dataField: "Ativo",
      text: "Status",
      align: "center",
      formatter: (_, row) => (row.Ativo ? "Ativo" : "Inativo"),
    }),
    columnTextField({
      dataField: "acoes",
      text: "Ações",
      align: "center",
      formatter: (_, row) => (
        <Box display="flex" justifyContent="center" style={{ gap: 4 }}>
          <IconButton
            size="small"
            onClick={() => {
              setEditingId(row.IdFranquiaMarketingPlaylist);
              setForm({
                nome: row.Nome,
                descricao: row.Descricao || "",
                ativo:
                  STATUS_OPTIONS.find((s) => s.value === row.Ativo) ||
                  STATUS_OPTIONS[0],
                ordem: String(row.Ordem),
              });
              setShowForm(true);
            }}
            aria-label="Editar"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <Button
            size="small"
            color="primary"
            onClick={() => setSelectedPlaylist(row)}
          >
            Itens
          </Button>
        </Box>
      ),
    }),
  ];

  const itemColumns = [
    columnTextField({ dataField: "TituloVideo", text: "Vídeo", align: "left" }),
    columnTextField({ dataField: "Ordem", text: "Ordem", align: "center" }),
    columnTextField({
      dataField: "acoes",
      text: "Ações",
      align: "center",
      formatter: (_, row) => (
        <Button
          size="small"
          color="secondary"
          onClick={() => handleRemoveItem(row)}
        >
          Remover
        </Button>
      ),
    }),
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Typography
          variant="h4"
          style={{ color: themeColorsDesingSystem.text.primary }}
        >
          Gestão de Playlists
        </Typography>
        <IconButton
          onClick={onClose}
          style={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {showForm ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <InputText
                label="Nome da playlist"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <InputText
                label="Descrição"
                value={form.descricao}
                onChange={(e) =>
                  setForm({ ...form, descricao: e.target.value })
                }
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={6}>
              <AutoCompleteField
                label="Status"
                value={form.ativo}
                onChange={(v) => setForm({ ...form, ativo: v })}
                options={STATUS_OPTIONS}
                optionLabelKey="label"
                optionIdKey="value"
              />
            </Grid>
            <Grid item xs={6}>
              <InputText
                label="Ordem"
                type="number"
                value={form.ordem}
                onChange={(e) => setForm({ ...form, ordem: e.target.value })}
              />
            </Grid>
          </Grid>
        ) : selectedPlaylist ? (
          <>
            <Typography variant="h4" style={{ marginBottom: 16 }}>
              Itens — {selectedPlaylist.Nome}
            </Typography>
            <Grid container spacing={2} style={{ marginBottom: 16 }}>
              <Grid item xs={12} md={8}>
                <AutoCompleteField
                  label="Adicionar vídeo"
                  value={videoToAdd}
                  onChange={setVideoToAdd}
                  options={videoOptions}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleAddVideo}
                  disabled={!videoToAdd}
                  style={{ height: "100%" }}
                >
                  Adicionar
                </Button>
              </Grid>
            </Grid>
            <ToolkitTable
              keyField="IdFranquiaMarketingPlaylistItem"
              dataTable={selectedPlaylist.Itens || []}
              columnModel={itemColumns}
              notFoundTitle="Nenhum vídeo na playlist."
            />
          </>
        ) : (
          <>
            <Box display="flex" justifyContent="flex-end" marginBottom={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingId(null);
                  setForm(EMPTY_FORM);
                  setShowForm(true);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                Nova playlist
              </Button>
            </Box>
            <ToolkitTable
              keyField="IdFranquiaMarketingPlaylist"
              dataTable={playlists}
              columnModel={playlistColumns}
              notFoundTitle={
                loading ? "Carregando..." : "Nenhuma playlist cadastrada."
              }
            />
          </>
        )}
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px" }}>
        {showForm ? (
          <>
            <Button
              onClick={() => setShowForm(false)}
              variant="outlined"
              disabled={saving}
            >
              Voltar
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              color="primary"
              disabled={saving}
            >
              Salvar
            </Button>
          </>
        ) : selectedPlaylist ? (
          <Button onClick={() => setSelectedPlaylist(null)} variant="outlined">
            Voltar
          </Button>
        ) : (
          <Button onClick={onClose} variant="outlined">
            Fechar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
